import { FieldChange } from '@/redux/postActivity/postActivity.types';
import { fieldLabel, formatValue } from './fieldFormat';
import TextSplitDiff from './TextSplitDiff';

const LONG_TEXT_FIELDS = new Set(['description']);

const EMPTY = <span className="italic text-muted-foreground">empty</span>;

function Value({ text, tone }: { text: string; tone: string }) {
  return (
    <td
      className={`whitespace-pre-wrap break-words px-3 py-2 align-top ${tone}`}
    >
      {text || EMPTY}
    </td>
  );
}

function ShortRow({
  change,
  labels,
}: {
  change: FieldChange;
  labels: Record<string, string>;
}) {
  return (
    <tr className="border-b last:border-0">
      <th className="w-40 px-3 py-2 text-left align-top text-xs font-medium text-muted-foreground">
        {fieldLabel(change.field, labels)}
      </th>
      <Value
        text={formatValue(change.before, labels)}
        tone="bg-red-50 text-red-900"
      />
      <Value
        text={formatValue(change.after, labels)}
        tone="bg-green-50 text-green-900"
      />
    </tr>
  );
}

function LongRow({
  change,
  labels,
}: {
  change: FieldChange;
  labels: Record<string, string>;
}) {
  return (
    <tr className="border-b last:border-0">
      <td colSpan={3} className="px-3 py-2">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          {fieldLabel(change.field, labels)}
        </p>
        <TextSplitDiff
          before={formatValue(change.before, labels)}
          after={formatValue(change.after, labels)}
        />
      </td>
    </tr>
  );
}

/** Only the fields that changed, before on the left and after on the right. */
export default function FieldChanges({
  changes,
  labels,
}: {
  changes: FieldChange[];
  labels: Record<string, string>;
}) {
  if (changes.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-neutral-50 text-xs text-muted-foreground">
          <tr>
            <th className="w-40 px-3 py-1.5 text-left font-medium">Field</th>
            <th className="px-3 py-1.5 text-left font-medium">Before</th>
            <th className="px-3 py-1.5 text-left font-medium">After</th>
          </tr>
        </thead>
        <tbody>
          {changes.map((change) =>
            LONG_TEXT_FIELDS.has(change.field) ? (
              <LongRow key={change.field} change={change} labels={labels} />
            ) : (
              <ShortRow key={change.field} change={change} labels={labels} />
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
